import { ExpandLess, ExpandMore } from "@mui/icons-material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import {
	Box,
	CircularProgress,
	FormControl,
	IconButton,
	InputBase,
	MenuItem,
	Select,
	SelectChangeEvent,
	SelectProps,
	Theme,
	Typography,
	debounce,
	styled,
} from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import RouterConstants from "../../../../constants/RouterConstants";
import iframeStore from "../../../../pages/IframeNew/iframe-store.tsx";
import DebouncedInput from "../../DebouncedInput/DebouncedInput.tsx";
import styles from "./TGDropDown.module.scss";

export type TGDropDownItem<T = any> = {
	key: string;
	value: T;
	icon?: any;
	disabled?: boolean;
	badge?: string;
	className?: string;
	subItems?: TGDropDownItem[];
	badgeClassName?: string;
	id?: string;
	label?: string;
	description?: string;
	extraData?: any;
};

export type TGDropDownProps<T> = {
	value?: T;
	onSearchChange?: (event: any) => void;
	onSubItemChange?: (subItem: TGDropDownItem) => void;
	onTailoredModelsChange?: (item: TGDropDownItem) => void;
	onFoundationModelsChange?: (item: TGDropDownItem) => void;
	loading?: boolean;
	foundationModels: TGDropDownItem<T>[];
	tailoredModels: TGDropDownItem<T>[];
	height?: string;
	menuMaxHeight?: string;
	menuMaxWidth?: string;
	width?: string;
	className?: string;
	itemsClassName?: string;
	disabled?: boolean;
	hideArrowIcon?: boolean;
	border?: string;
	icon?: any;
	searchable?: boolean;
	debounceSearch?: boolean;
	SearchPlaceholder?: string;
	searchIcon?: boolean;
	hideTG?: boolean;
} & SelectProps<T>;

const TGDropdown = <T,>({
	value,
	onSearchChange,
	onSubItemChange,
	onTailoredModelsChange,
	onFoundationModelsChange,
	loading,
	foundationModels,
	tailoredModels,
	width,
	height,
	menuMaxHeight,
	menuMaxWidth,
	className,
	itemsClassName,
	disabled,
	hideArrowIcon,
	border,
	icon,
	searchable = false,
	debounceSearch = false,
	SearchPlaceholder = "",
	searchIcon = false,
	hideTG = false,
	...rest
}: TGDropDownProps<T>) => {
	const [selectedFoundationModel, setSelectedFoundationModel] = useState<any>();
	const [selectedTailoredModel, setSelectedTailoredModel] = useState<any>();
	const [openTailoredModels, setOpenTailoredModels] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);
	const tailoredModelsRef = useRef(null);
	const { t } = useTranslation("translation", {
		keyPrefix: "playground.textToImage.config.models.dropdownLabels",
	});

	useEffect(() => {
		if (value !== null && value !== undefined) {
			let selectedModel = foundationModels.find((item) => item.value === value);
			if (selectedModel) {
				setSelectedFoundationModel(selectedModel);
			} else {
				selectedModel = tailoredModels.find((item) => item.value === value);
				if (selectedModel) {
					setSelectedFoundationModel(foundationModels.find((item) => item.value === "TailoredModels"));
					setSelectedTailoredModel(selectedModel);
				}
			}
		}
	}, [value, foundationModels]);

	useEffect(() => {
		if (tailoredModels.length) {
			if (inputRef.current) {
				inputRef.current.focus();
			}
		}
	}, [tailoredModels]);

	const hasSelectedValue = selectedFoundationModel !== undefined && selectedFoundationModel !== null;

	const openCreateProject = () => {
		window.open(RouterConstants.NEW_TG.PROJECTS.fullPath, "_blank");
	};

	const toggleTailoredModels = () => {
		setOpenTailoredModels((prev) => (prev ? false : true));
	};

	const handleFoundationModelSelect = (e: SelectChangeEvent<T>) => {
		const selectedModel = foundationModels.find((item) => item.id === e.target.value);
		if (!selectedModel) return;
		setSelectedFoundationModel(selectedModel);
		if (e.target.value !== "TailoredModels") {
			setSelectedTailoredModel(null);
		}
		onFoundationModelsChange && onFoundationModelsChange?.(selectedModel);
	};

	const handleTailoredModelselect = (model: TGDropDownItem) => {
		const selectedModel = foundationModels.find((item) => item.id === "TailoredModels");
		setSelectedFoundationModel(selectedModel);
		setSelectedTailoredModel(model);
		toggleTailoredModels();
		onTailoredModelsChange && onTailoredModelsChange?.(model);
	};

	const dropdownRenderValue = () => {
		return (
			<>
				<Box className={styles.inputStyle}>
					{icon}
					<Typography className={styles.lableStyle}>
						{selectedTailoredModel ? selectedTailoredModel.key : selectedFoundationModel.key}
					</Typography>
				</Box>
			</>
		);
	};

	const dropDownItem = (item: TGDropDownItem) => {
		const menuItemProps = {
			key: item.id,
			value: item.value as any,
			disabled: item.disabled || disabled,
			className: clsx(itemsClassName, item.className, {
				[styles.itemWithDescription]: !!item.description,
				[styles.TGItemsWrapper]: item.id === "TailoredModels",
			}),
		};

		const content = (
			<>
				<Box className={styles.itemWrap}>
					{item.icon ? (
						<Box className={styles.itemContainer}>
							{item.icon}
							{item.key}
						</Box>
					) : (
						<span className={styles.itemText} dangerouslySetInnerHTML={{ __html: item.key }} />
					)}
					{item.badge && <Box className={clsx(item.badgeClassName, styles.badge)}>{item.badge}</Box>}
				</Box>
				{item.description && <Typography className={styles.optionsDescription}>{item.description}</Typography>}
			</>
		);

		return item.id === "TailoredModels" ? (
			!hideTG && (
				<StyledMenuItem ref={tailoredModelsRef} {...menuItemProps}>
					<Box key={item.id} className={styles.TGItems}>
						<Box
							className={styles.white_bg}
							onClick={(e) => {
								e.stopPropagation();
								toggleTailoredModels();
								scrollToSelectedTG();
							}}
						>
							<Box
								className={clsx(styles.TGItemsTitle, {
									[styles.selected]: !!selectedTailoredModel,
								})}
							>
								{item.key}
								<IconButton size="small">
									{openTailoredModels ? <ExpandLess /> : <ExpandMore />}
								</IconButton>
							</Box>
						</Box>

						{openTailoredModels && (
							<>
								{searchable && (
									<MenuItem
										onClick={(e) => e.stopPropagation()}
										className={clsx(styles.subItem, styles.searchField)}
									>
										<DebouncedInput
											onChange={onSearchChange}
											className={styles.customBriaInput}
											debounceDuration={debounceSearch ? 500 : 0}
											onKeyDown={(e) => e.stopPropagation()}
											onClick={(e) => e.stopPropagation()}
											height="36px"
											searchIcon={searchIcon}
											placeHolder={SearchPlaceholder}
											inputRef={inputRef}
										/>
									</MenuItem>
								)}
								{tailoredModels.map((taliredModel) => (
									<MenuItem
										key={taliredModel.value as string}
										onClick={() => handleTailoredModelselect(taliredModel)}
										className={clsx(styles.TGItem, {
											[styles.selected]: taliredModel.value === selectedTailoredModel?.value,
										})}
									>
										<Box className={styles.itemWrap} id={taliredModel.value as string}>
											{taliredModel.icon ? (
												<Box className={styles.itemContainer}>
													{taliredModel.icon}
													{taliredModel.key}
												</Box>
											) : (
												<span
													className={styles.itemText}
													dangerouslySetInnerHTML={{ __html: taliredModel.key }}
												/>
											)}
											{taliredModel.badge && (
												<Box className={clsx(taliredModel.badgeClassName, styles.badge)}>
													{taliredModel.badge}
												</Box>
											)}
										</Box>
										{taliredModel.description && (
											<Typography className={styles.optionsDescription}>
												{taliredModel.description}
											</Typography>
										)}
									</MenuItem>
								))}
								{!iframeStore.isIframe() && (
									<MenuItem className={styles.createTM} onClick={openCreateProject}>
										{t("createTailoredModel")}
									</MenuItem>
								)}
							</>
						)}
					</Box>
				</StyledMenuItem>
			)
		) : (
			<StyledMenuItem {...menuItemProps} key={item.id}>
				{content}
			</StyledMenuItem>
		);
	};

	const scrollToSelectedTG = () => {
		debounce(() => {
			const selectedValue = tailoredModels.find((item) => item.value === selectedTailoredModel?.value);
			const dropdownElement = tailoredModelsRef?.current as HTMLElement | null;
			if (selectedValue && dropdownElement) {
				const selectedTG = dropdownElement.querySelector(`#${CSS.escape(selectedValue.value as string)}`);
				if (selectedTG instanceof HTMLElement) {
					selectedTG.scrollIntoView({ behavior: "smooth", block: "center" });
				}
			}
		}, 100)();
	};

	const handleOpen = () => {
		if (selectedTailoredModel) {
			setOpenTailoredModels(() => {
				return true;
			});
			scrollToSelectedTG();
		} else {
			setOpenTailoredModels(false);
		}
	};

	return (
		<StyledFormControl
			className={clsx(className, styles.formControl)}
			width={width}
			height={selectedFoundationModel?.length > 0 ? "auto" : height}
		>
			<StyledSelect
				displayEmpty={!!rest.placeholder}
				value={typeof value === "number" && isNaN(value) ? "" : value ?? ""}
				IconComponent={loading ? DropDownLoading : ExpandMoreRoundedIcon}
				hideArrowIcon={hideArrowIcon || (disabled && !loading)}
				disabled={disabled}
				renderValue={() =>
					disabled
						? value?.toString() ?? rest.placeholder
						: hasSelectedValue
						? dropdownRenderValue()
						: rest.placeholder
				}
				input={border ? <SelectInputBase border={border} /> : undefined}
				classes={{
					root: disabled ? styles.disabledSelect : "",
				}}
				onChange={handleFoundationModelSelect}
				onOpen={handleOpen}
				{...rest}
			>
				{loading && <StyledMenuItem disabled>Loading..</StyledMenuItem>}
				{foundationModels.map((item: TGDropDownItem) => dropDownItem(item))}
				{rest.children && <StyledMenuItem>{rest.children}</StyledMenuItem>}
			</StyledSelect>
		</StyledFormControl>
	);
};

export default observer(TGDropdown);

const StyledFormControl = styled(FormControl)(({ width, height }: { width?: string; height?: string }) => ({
	width: width ?? "auto",
	"& .MuiInputBase-root": {
		height: height ?? "40px",
		minWidth: "120px",
		fontSize: "14px",
		fontWeight: "400",
		justifyContent: "center",
	},
	"& .MuiSelect-select": {
		minHeight: "16px",
		padding: "12px 14px",
	},
}));

const SelectInputBase = styled(InputBase)(({ border }: { border?: string }) => ({
	border: border ? border : "initial",
	"& > div": {
		padding: "16.5px 14px",
	},
}));

type CustomSelectProps = SelectProps<any> & {
	hideArrowIcon?: boolean;
};

const StyledSelect = styled(Select<any>, { shouldForwardProp: (props) => props != "hideArrowIcon" })<CustomSelectProps>(
	({ theme, hideArrowIcon }: { theme: Theme; hideArrowIcon?: boolean }) => ({
		width: "auto",
		"&.Mui-focused": {
			".MuiSvgIcon-root": {
				fill: theme.palette.mode === "dark" ? "var(--secondary-color)" : theme.palette.primary.light,
			},
		},
		".MuiSelect-icon": {
			display: hideArrowIcon ? "none" : "block",
		},
	}),
);

const StyledMenuItem = styled(MenuItem)({
	fontWeight: 500,
	paddingTop: 8,
	paddingBottom: 8,
	fontSize: "14px",
	color: "var(--secondary-color)",
	textWrap: "wrap",
	"&.Mui-selected": {
		color: "var(--primary-text-color)",
		fontWeight: "bold",
		backgroundColor: "var(--mui-menuitem-background-selected) !important",
	},
});

const DropDownLoading = styled(CircularProgress)(({ theme }) => ({
	color: `${theme.palette.primary.light} !important`,
	width: "1em !important",
	height: "1em !important",
	right: "13px !important",
}));
